import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { UserPreferencesProvider } from './preferences/UserPreferences';
import Layout from './components/layout/Layout';
import Login from './pages/Login';

import LogViewer from './components/LogViewer';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AddressUpdatePortal from './pages/AddressUpdatePortal';
import RequestReturn from './pages/RequestReturn';
import LandingRoute from './components/LandingRoute';
import NavGuard from './components/NavGuard';
import { roleTier } from './config/roles';

/** Shown while a lazily-loaded route chunk is being fetched. */
const RouteFallback = () => (
  <div className="flex min-h-[60vh] items-center justify-center">
    <div
      className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600"
      role="status"
      aria-label="Loading"
    />
  </div>
);

/**
 * Routes are code-split.
 *
 * Every page used to be a static import, so the build emitted ONE chunk of
 * about 1.5 MB and the login screen downloaded CompanySettings, the repricing
 * dashboard, the carrier schemas and every icon before it could render. On a
 * connection with a few hundred milliseconds of round-trip that is most of the
 * time to first paint, and it is paid again on every deploy because the file
 * hash changes.
 *
 * Lazy means each route ships its own chunk, fetched when someone navigates to
 * it. The shell below (Layout) and the four unauthenticated screens stay eager
 * — they are needed immediately and splitting them would only add a round trip.
 */
const AdminBillingHistory = React.lazy(() => import('./pages/AdminBillingHistory'));
const AdminCarriers = React.lazy(() => import('./pages/AdminCarriers'));
const AdminCompanies = React.lazy(() => import('./pages/AdminCompanies'));
const AdminCompanyBillingHistory = React.lazy(() => import('./pages/AdminCompanyBillingHistory'));
const AdminCompanyDetail = React.lazy(() => import('./pages/AdminCompanyDetail'));
const AdminNotifications = React.lazy(() => import('./pages/AdminNotifications'));
const AdminTrackingSources = React.lazy(() => import('./pages/AdminTrackingSources'));
const AdminUsers = React.lazy(() => import('./pages/AdminUsers'));
const AutoBatchLogs = React.lazy(() => import('./pages/AutoBatchLogs'));
const BillingReport = React.lazy(() => import('./pages/BillingReport'));
// P&L Transactions: the P&L popups' "Open full report", under the Billing Report's menu.
const BillingPnlReport = React.lazy(() => import('./pages/BillingPnlReport'));
const GoogleAds = React.lazy(() => import('./pages/GoogleAds'));
const AmazonAds = React.lazy(() => import('./pages/AmazonAds'));
const EbayAds = React.lazy(() => import('./pages/EbayAds'));
const BoxManagement = React.lazy(() => import('./pages/BoxManagement.tsx'));
const CarrierSettings = React.lazy(() => import('./pages/CarrierSettings'));
const CompanyRoles = React.lazy(() => import('./pages/CompanyRoles'));
const CompanySettings = React.lazy(() => import('./pages/CompanySettings'));
const CompanySubscription = React.lazy(() => import('./pages/CompanySubscription'));
const CompanyUsers = React.lazy(() => import('./pages/CompanyUsers'));
const CompetitorPrices = React.lazy(() => import('./pages/CompetitorPrices'));
const CreateRepricingBatchPage = React.lazy(() => import('./pages/CreateRepricingBatchPage'));
const CronSchedules = React.lazy(() => import('./pages/CronSchedules'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const EmailTemplateForm = React.lazy(() => import('./pages/EmailTemplateForm'));
const EmailTemplatesList = React.lazy(() => import('./pages/EmailTemplatesList'));
const ExternalOrderDetails = React.lazy(() => import('./pages/ExternalOrderDetails'));
const ExternalOrders = React.lazy(() => import('./pages/ExternalOrders'));
const ShipTogether = React.lazy(() => import('./pages/ShipTogether'));
const FacilityShipments = React.lazy(() => import('./pages/FacilityShipments'));
const FacilityScoreboard = React.lazy(() => import('./pages/FacilityScoreboard'));
const GetQuotation = React.lazy(() => import('./pages/GetQuotation'));
const InternalExpediting = React.lazy(() => import('./pages/InternalExpediting'));
const InternalTicketDetails = React.lazy(() => import('./pages/InternalTicketDetails'));
const InternalTickets = React.lazy(() => import('./pages/InternalTickets'));
const LogDetails = React.lazy(() => import('./pages/LogDetails'));
const ModifyConnection = React.lazy(() => import('./pages/ModifyConnection'));
const MyReviewQueue = React.lazy(() => import('./pages/MyReviewQueue'));
const MySales = React.lazy(() => import('./pages/MySales'));
const MyOrders = React.lazy(() => import('./pages/MyOrders'));
const NotAllowed = React.lazy(() => import('./pages/NotAllowed'));
const NotificationRules = React.lazy(() => import('./pages/NotificationRules'));
const OrderIntegrations = React.lazy(() => import('./pages/OrderIntegrations'));
const PackagingLogs = React.lazy(() => import('./pages/PackagingLogs'));
const PackingSlip = React.lazy(() => import('./pages/PackingSlip'));
const PaymentConfiguration = React.lazy(() => import('./pages/PaymentConfiguration'));
const ProductReviews = React.lazy(() => import('./pages/ProductReviews'));
const ListingSuggestions = React.lazy(() => import('./pages/ListingSuggestions'));
const ReviewQuestions = React.lazy(() => import('./pages/ReviewQuestions'));
const Products = React.lazy(() => import('./pages/Products'));
const Profile = React.lazy(() => import('./pages/Profile'));
const PurchaseOrderForm = React.lazy(() => import('./pages/PurchaseOrderForm'));
const PurchaseOrderView = React.lazy(() => import('./pages/PurchaseOrderView'));
const Suppliers = React.lazy(() => import('./pages/Suppliers'));
const Reorder = React.lazy(() => import('./pages/Reorder'));
const PurchaseOrders = React.lazy(() => import('./pages/PurchaseOrders'));
const RateRequestDetail = React.lazy(() => import('./pages/RateRequestDetail'));
const RateRequestsReport = React.lazy(() => import('./pages/RateRequestsReport'));
const RepricingDashboard = React.lazy(() => import('./pages/RepricingDashboard'));
const SkuMargins = React.lazy(() => import('./pages/SkuMargins'));
const ReviewForm = React.lazy(() => import('./pages/ReviewForm'));
const SalesAnalytics = React.lazy(() => import('./pages/SalesAnalytics'));
const ShipmentDetails = React.lazy(() => import('./pages/ShipmentDetails'));
const SmsLogs = React.lazy(() => import('./pages/SmsLogs'));
const SmsTemplateForm = React.lazy(() => import('./pages/SmsTemplateForm'));
const SmsTemplatesList = React.lazy(() => import('./pages/SmsTemplatesList'));
const SmtpLogs = React.lazy(() => import('./pages/SmtpLogs'));
const SubscriptionPlans = React.lazy(() => import('./pages/SubscriptionPlans'));
const Support = React.lazy(() => import('./pages/Support'));
const SystemLogDetail = React.lazy(() => import('./pages/SystemLogDetail'));
const SystemSettings = React.lazy(() => import('./pages/SystemSettings'));
const TagManagement = React.lazy(() => import('./pages/TagManagement'));
const TrackEventDetails = React.lazy(() => import('./pages/TrackEventDetails'));
const TrackNumberHistory = React.lazy(() => import('./pages/TrackNumberHistory'));
const TrackingMocks = React.lazy(() => import('./pages/TrackingMocks'));
const TransitLocations = React.lazy(() => import('./pages/TransitLocations'));
const TrackingEvents = React.lazy(() => import('./pages/TrackingEvents'));
const WarehouseManagement = React.lazy(() => import('./pages/WarehouseManagement'));
const Vendors = React.lazy(() => import('./pages/Vendors'));
const Returns = React.lazy(() => import('./pages/Returns'));
const ReturnForm = React.lazy(() => import('./pages/ReturnForm'));
const ReturnView = React.lazy(() => import('./pages/ReturnView'));
const MarketplaceSheetRecords = React.lazy(() => import('./pages/MarketplaceSheetRecords'));
const Inventory = React.lazy(() => import('./pages/Inventory'));
const Warehouse = React.lazy(() => import('./pages/Warehouse'));
const PriceFiles = React.lazy(() => import('./pages/PriceFiles'));


const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const RoleProtectedRoute = ({ children, role }: { children: React.ReactNode, role: string }) => {
  const { user, isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== role) return <Navigate to="/" replace />;
  return children;
};

const FeatureProtectedRoute = ({
  children,
  menuId,
  feature,
  features,
  roles,
}: {
  children: React.ReactNode;
  /** Role menu id(s) that open this page; any one of them is enough. */
  menuId?: string | string[];
  feature?: string;
  features?: string[];
  roles?: string[];
}) => {
  const { user, isAuthenticated, hasFeature, hasMenuPermission } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles && user?.role) {
    // By tier, never by role name (config/roles.ts): any member role string
    // opens a route to every member - which member role it is, is a matter of
    // its menus, checked below. A SubAdmin goes where members or admins go;
    // the menu check (and the API) holds them to what their role ticks.
    const tier = roleTier(user.role);
    const tiers = roles.map(roleTier);
    const isAllowedRole =
      tiers.includes(tier) ||
      (tier === 'sub_admin' && (tiers.includes('member') || tiers.includes('company_admin')));
    if (!isAllowedRole) return <Navigate to="/" replace />;
  }

  if (menuId) {
    const ids = Array.isArray(menuId) ? menuId : [menuId];
    // A page the role does not include: say so, rather than quietly landing
    // somewhere else. The Not Allowed page offers the way to a permitted one.
    if (!ids.some((id) => hasMenuPermission(id))) return <Navigate to="/not-allowed" replace />;
  }

  if (feature && !hasFeature(feature)) return <Navigate to="/" replace />;
  if (features && features.length > 0) {
    const hasAnyFeature = features.some((f) => hasFeature(f));
    if (!hasAnyFeature) return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <UserPreferencesProvider>
      <Router>
        <ToastContainer position="top-right" autoClose={3000} />
        {/* Required by the lazy routes above: React needs somewhere to render
            while a route's chunk is in flight. Deliberately a plain centred
            spinner rather than a skeleton — it is usually visible for a few
            hundred milliseconds on first visit to a route and never again. */}
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/address-update" element={<AddressUpdatePortal />} />
          <Route path="/request-return" element={<RequestReturn />} />
          
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<LandingRoute dashboard={<Dashboard />} />} />
            {/* Its own page, not a stand-in for `/`: sharing the Dashboard's path
                made the queue unreachable for anyone who also has a Dashboard. */}
            <Route path="my-queue" element={<NavGuard menu={'my-queue'}><MyReviewQueue /></NavGuard>} />
            {/* My Sales: your own numbers only - the API never returns anyone
                else's. Comparing people is Vertical Sales and Analytics. */}
            <Route path="my-sales" element={<NavGuard menu={'my-sales'}><MySales /></NavGuard>} />
            <Route path="my-orders" element={<NavGuard menu={'my-orders'}><MyOrders /></NavGuard>} />
            <Route path="review/:listingId" element={<ReviewForm />} />
            <Route path="profile" element={<Profile />} />
            <Route path="not-allowed" element={<NotAllowed />} />

            <Route path="quotation" element={<NavGuard menu={'quotation'}><GetQuotation /></NavGuard>} />
            <Route path="shipment/:id/track/:carrier/:trackingNumber" element={<NavGuard menu={['track-history', 'facility-shipments', 'orders']}><TrackEventDetails /></NavGuard>} />
            <Route path="shipment/:id" element={<NavGuard menu={['track-history', 'facility-shipments', 'orders']}><ShipmentDetails /></NavGuard>} />
            {/* /history listed shipments and was reachable only by typing the
                URL — no sidebar entry, one stale link. Kept as a redirect rather
                than deleted outright so anything bookmarked still lands on the
                live equivalent instead of the dashboard. */}
            <Route path="history" element={<Navigate to="/track-history" replace />} />
            <Route path="track-history" element={<NavGuard menu={'track-history'}><TrackNumberHistory /></NavGuard>} />
            <Route path="facility-shipments" element={<NavGuard menu={'facility-shipments'}><FacilityShipments /></NavGuard>} />
            <Route path="facility-scoreboard" element={<NavGuard menu={'facility-scoreboard'}><FacilityScoreboard /></NavGuard>} />
            <Route path="support" element={<NavGuard menu="support"><Support /></NavGuard>} />
            <Route path="internal-tickets" element={<NavGuard menu={'tickets'}><InternalTickets /></NavGuard>} />
            <Route path="internal-tickets/:id" element={<NavGuard menu={'tickets'}><InternalTicketDetails /></NavGuard>} />
            <Route path="billing-report" element={<NavGuard menu={'billing-report'}><BillingReport /></NavGuard>} />
            <Route path="google-ads" element={<NavGuard menu={'google-ads'}><GoogleAds /></NavGuard>} />
            <Route path="amazon-ads" element={<NavGuard menu={'amazon-ads'}><AmazonAds /></NavGuard>} />
            <Route path="ebay-ads" element={<NavGuard menu={'ebay-ads'}><EbayAds /></NavGuard>} />
            <Route path="billing-report/pnl" element={<NavGuard menu={'billing-report'}><BillingPnlReport /></NavGuard>} />
            <Route path="sales-analytics" element={<NavGuard menu={'sales-analytics'}><SalesAnalytics /></NavGuard>} />
            {/* Anybody's numbers. Its own-numbers twin, My Sales, sits in the Overview hub above. */}
            <Route path="transit-info" element={<NavGuard menu={'transit'}><TransitLocations /></NavGuard>} />
            <Route path="tracking-events" element={<NavGuard menu={'tracking-events'}><TrackingEvents /></NavGuard>} />
            <Route path="internal-expediting" element={<NavGuard menu={'expediting'}><InternalExpediting /></NavGuard>} />
            <Route path="settings/subscription" element={<FeatureProtectedRoute roles={['company_admin', 'super_admin', 'company_user']}><CompanySubscription /></FeatureProtectedRoute>} />
            <Route path="settings/carriers" element={<NavGuard menu={'carriers'}><CarrierSettings /></NavGuard>} />
            <Route path="settings/integrations" element={<NavGuard menu={'order-integrations'}><OrderIntegrations /></NavGuard>} />
            <Route path="settings/integrations/:id" element={<NavGuard menu={'order-integrations'}><ModifyConnection /></NavGuard>} />
            {/* Product List and Tag Management follow the role's assigned menus:
                admins always, any other role when its menus include them. The
                API still limits writes (sync, import, edit, tag assign) to
                admins, so for other roles these pages are view-only. */}
            <Route path="products" element={<NavGuard menu={'products'}><Products /></NavGuard>} />
            <Route path="repricing/create" element={<NavGuard menu={'repricing'}><CreateRepricingBatchPage /></NavGuard>} />
            <Route path="repricing" element={<NavGuard menu={'repricing'}><RepricingDashboard /></NavGuard>} />
            <Route path="competitor-prices" element={<NavGuard menu={'competitor-prices'}><CompetitorPrices /></NavGuard>} />
            <Route path="sku-margins" element={<NavGuard menu={'sku-margins'}><SkuMargins /></NavGuard>} />
            <Route path="product-reviews" element={<NavGuard menu={'product-reviews'}><ProductReviews /></NavGuard>} />
            <Route path="listing-suggestions" element={<NavGuard menu={'listing-suggestions'}><ListingSuggestions /></NavGuard>} />
            <Route path="tags" element={<NavGuard menu={'tags'}><TagManagement /></NavGuard>} />
            <Route path="purchase-orders" element={<NavGuard menu={'purchase-orders'}><PurchaseOrders /></NavGuard>} />
            <Route path="purchase-orders/suppliers" element={<NavGuard menu={'purchase-orders'}><Suppliers /></NavGuard>} />
            <Route path="reorder" element={<NavGuard menu={'reorder'}><Reorder /></NavGuard>} />
            <Route path="purchase-orders/new" element={<NavGuard menu={'purchase-orders'}><PurchaseOrderForm /></NavGuard>} />
            <Route path="purchase-orders/:id/edit" element={<NavGuard menu={'purchase-orders'}><PurchaseOrderForm /></NavGuard>} />
            <Route path="purchase-orders/:id" element={<NavGuard menu={'purchase-orders'}><PurchaseOrderView /></NavGuard>} />
            <Route path="vendors" element={<NavGuard menu={'vendors'}><Vendors /></NavGuard>} />
            <Route path="inventory" element={<NavGuard menu={'inventory'}><Inventory /></NavGuard>} />
            {/* Every WMS page is granted by the one `wms` role menu, so any WMS
                entry guards them all; Receive's own action is checked on the page. */}
            <Route path="wms" element={<NavGuard menu={'wms-today'}><Warehouse /></NavGuard>} />
            <Route path="wms/:tab" element={<NavGuard menu={'wms-today'}><Warehouse /></NavGuard>} />
            <Route path="price-files" element={<NavGuard menu={'price-files'}><PriceFiles /></NavGuard>} />
            <Route path="external-orders" element={<NavGuard menu={'orders'}><ExternalOrders /></NavGuard>} />
            {/* Ship Together: opened from the grid with ?ids=a,b, several orders of one customer in one box. */}
            <Route path="external-orders/ship-together" element={<NavGuard menu={'orders'} action="label.create"><ShipTogether /></NavGuard>} />
            <Route path="returns" element={<NavGuard menu={'returns'}><Returns /></NavGuard>} />
            {/* Raising a return is offered on the order page too, so the form opens with either menu. */}
            <Route path="returns/new" element={<NavGuard menu={['returns', 'orders']} action="returns.create"><ReturnForm /></NavGuard>} />
            <Route path="returns/marketplace-sheet" element={<NavGuard menu={'returns'}><MarketplaceSheetRecords /></NavGuard>} />
            <Route path="returns/:id" element={<NavGuard menu={'returns'}><ReturnView /></NavGuard>} />
            {/* Reached from every page that links to an order, so any of them opens it - with the role's "Open order details". */}
            <Route path="external-orders/:id" element={<NavGuard menu={['orders', 'track-history', 'facility-shipments', 'my-orders', 'expediting']} action="orders.view"><ExternalOrderDetails /></NavGuard>} />
            <Route path="settings/company" element={<NavGuard menu={'company-settings'}><CompanySettings /></NavGuard>} />
            <Route path="settings/review-questions" element={<NavGuard menu={'review-questions'}><ReviewQuestions /></NavGuard>} />
            <Route path="settings/auto-batch-logs" element={<NavGuard menu={'auto-batch-logs'}><AutoBatchLogs /></NavGuard>} />
            <Route path="settings/packaging-logs" element={<NavGuard menu={'packaging-logs'}><PackagingLogs /></NavGuard>} />
            <Route path="settings/smtp-logs" element={<NavGuard menu={'smtp-logs'}><SmtpLogs isAdminView={false} /></NavGuard>} />
            <Route path="settings/sms-logs" element={<NavGuard menu={'sms-logs'}><SmsLogs isAdminView={false} /></NavGuard>} />
            <Route path="settings/rate-requests" element={<NavGuard menu={'rate-requests'}><RateRequestsReport /></NavGuard>} />
            <Route path="settings/cron-schedules" element={<NavGuard menu={'cron-schedules'}><CronSchedules isAdminView={false} /></NavGuard>} />
            <Route path="settings/tracking-mocks" element={<NavGuard menu={'tracking-mocks'}><TrackingMocks /></NavGuard>} />
            <Route path="settings/email-templates" element={<NavGuard menu={'email-templates'}><EmailTemplatesList /></NavGuard>} />
            <Route path="settings/email-templates/:id" element={<NavGuard menu={'email-templates'}><EmailTemplateForm /></NavGuard>} />
            <Route path="settings/sms-templates" element={<NavGuard menu={'sms-templates'}><SmsTemplatesList /></NavGuard>} />
            <Route path="settings/sms-templates/:id" element={<NavGuard menu={'sms-templates'}><SmsTemplateForm /></NavGuard>} />
            <Route path="settings/boxes" element={<NavGuard menu={'boxes'}><BoxManagement /></NavGuard>} />
            <Route path="settings/rate-requests/:id" element={<NavGuard menu={'rate-requests'}><RateRequestDetail /></NavGuard>} />
            <Route path="settings/team" element={<NavGuard menu={'team'}><CompanyUsers /></NavGuard>} />
            <Route path="settings/facilities" element={<NavGuard menu={'facilities'}><WarehouseManagement /></NavGuard>} />
            {/* Old path for Facilities (one page, formerly two menus). */}
            <Route path="settings/warehouses" element={<Navigate to="/settings/facilities" replace />} />
            <Route path="settings/roles" element={<NavGuard menu="roles"><CompanyRoles /></NavGuard>} />
            <Route path="admin/users" element={<RoleProtectedRoute role="super_admin"><AdminUsers /></RoleProtectedRoute>} />
            <Route path="admin/companies" element={<RoleProtectedRoute role="super_admin"><AdminCompanies /></RoleProtectedRoute>} />
            <Route path="admin/companies/:companyId/billing-history" element={<RoleProtectedRoute role="super_admin"><AdminCompanyBillingHistory /></RoleProtectedRoute>} />
            <Route path="admin/companies/:id" element={<RoleProtectedRoute role="super_admin"><AdminCompanyDetail /></RoleProtectedRoute>} />
            <Route path="admin/carriers" element={<RoleProtectedRoute role="super_admin"><AdminCarriers /></RoleProtectedRoute>} />
            <Route path="admin/tracking-sources" element={<RoleProtectedRoute role="super_admin"><AdminTrackingSources /></RoleProtectedRoute>} />

            <Route path="admin/monitoring/log/:id" element={<RoleProtectedRoute role="super_admin"><LogDetails /></RoleProtectedRoute>} />
            <Route path="admin/system-logs" element={<RoleProtectedRoute role="super_admin"><LogViewer /></RoleProtectedRoute>} />
            <Route path="admin/system-logs/entry" element={<RoleProtectedRoute role="super_admin"><SystemLogDetail /></RoleProtectedRoute>} />
            <Route path="admin/rate-requests" element={<RoleProtectedRoute role="super_admin"><RateRequestsReport /></RoleProtectedRoute>} />
            <Route path="admin/rate-requests/:id" element={<RoleProtectedRoute role="super_admin"><RateRequestDetail /></RoleProtectedRoute>} />
            <Route path="admin/smtp-logs" element={<RoleProtectedRoute role="super_admin"><SmtpLogs isAdminView={true} /></RoleProtectedRoute>} />
            <Route path="admin/sms-logs" element={<RoleProtectedRoute role="super_admin"><SmsLogs isAdminView={true} /></RoleProtectedRoute>} />
            <Route path="admin/cron-schedules" element={<RoleProtectedRoute role="super_admin"><CronSchedules isAdminView={true} /></RoleProtectedRoute>} />
            <Route path="admin/email-templates" element={<RoleProtectedRoute role="super_admin"><EmailTemplatesList /></RoleProtectedRoute>} />
            <Route path="admin/email-templates/:id" element={<RoleProtectedRoute role="super_admin"><EmailTemplateForm /></RoleProtectedRoute>} />
            <Route path="admin/sms-templates" element={<RoleProtectedRoute role="super_admin"><SmsTemplatesList /></RoleProtectedRoute>} />
            <Route path="admin/sms-templates/:id" element={<RoleProtectedRoute role="super_admin"><SmsTemplateForm /></RoleProtectedRoute>} />
            <Route path="admin/system-settings" element={<RoleProtectedRoute role="super_admin"><SystemSettings /></RoleProtectedRoute>} />
            <Route path="admin/notifications" element={<RoleProtectedRoute role="super_admin"><AdminNotifications /></RoleProtectedRoute>} />
            <Route path="admin/notification-rules" element={<RoleProtectedRoute role="super_admin"><NotificationRules /></RoleProtectedRoute>} />
            <Route path="admin/settings/payment" element={<RoleProtectedRoute role="super_admin"><PaymentConfiguration /></RoleProtectedRoute>} />
            <Route path="admin/subscription-plans" element={<RoleProtectedRoute role="super_admin"><SubscriptionPlans /></RoleProtectedRoute>} />
            <Route path="admin/billing-history" element={<RoleProtectedRoute role="super_admin"><AdminBillingHistory /></RoleProtectedRoute>} />

            <Route path="shipment/:id/packing-slip/:trackingNumber" element={<PackingSlip />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </Router>
      </UserPreferencesProvider>
    </AuthProvider>
  );
}


export default App;
